import {useState, useCallback, useMemo} from 'react';
import {
    getId,
    calcRowTotal,
    boundsOf,
    isSelectable,
    validateQty,
    validatePrice
} from '@Local/Orders/Componetnets/SalesAllowanceItemTable/SalesAllowanceItemTable.util';
import {isEmpty, isFunction} from '@Util/utils';

/**
 * useSalesAllowance
 *
 * 管理「訂單折讓表格」中單筆或多筆商品的勾選、輸入與計算的自訂 Hook。
 *
 * 功能：
 * - 勾選／取消勾選商品，並初始化或重置其折讓金額／數量
 * - 管理「可調價格」與「可調數量」兩種模式
 * - 驗證輸入值是否合法（價格、數量是否在允許範圍內）
 * - 即時計算列小計與整體總計
 * - 提供「全選」「部分選取」「是否顯示編輯模式」等衍生狀態
 * - 允許透過 onAggregate 回傳合計與狀態
 *
 * @param {Array<Object>} items - 訂單商品清單，每筆需包含：
 *   - orderItemID, orderID
 *   - productName, productTypeText
 *   - salePrice, quantity
 *   - returnPrice, returnQuantity
 *   - onlyAllowancePrice (boolean，true = 僅金額可調)
 * @param {Object} [options] - 設定參數
 * @param {boolean} [options.listDisabled=false] - 是否停用整份清單的選取與輸入
 * @param {Function} [options.onAggregate] - 合計與狀態回呼 (total, { selectedIds, priceById, qtyById, errorById })
 *
 * @returns {Object} 狀態與操作方法
 *
 * 狀態：
 * @returns {Set<string|number>} return.selectedIds - 已選取商品 ID 集合
 * @returns {Object.<number>} return.priceById - 各商品輸入的折讓金額
 * @returns {Object.<number>} return.qtyById - 各商品輸入的折讓數量
 * @returns {Object.<boolean>} return.errorById - 各商品輸入是否合法（true = 錯誤）
 * @returns {boolean} return.isAllChecked - 是否全選
 * @returns {boolean} return.isIndeterminate - 是否部分選取
 * @returns {boolean} return.showSelectedMode - 是否進入「已選取模式」（有勾選商品）
 * @returns {number} return.grandTotal - 所有選取列的小計加總
 *
 * 操作方法：
 * @returns {Function} return.toggleAll - 勾選或取消全選 (toggleAll(checked: boolean))
 * @returns {Function} return.toggleOne - 勾選或取消單筆 (toggleOne(id, checked, row?))
 * @returns {Function} return.onChangePrice - 更新單筆折讓金額 (onChangePrice(row, value))
 * @returns {Function} return.onChangeQty - 更新單筆折讓數量 (onChangeQty(row, value))
 *
 * 工具方法：
 * @returns {Function} return.rowTotal - 計算單筆商品的小計 (rowTotal(row))
 * @returns {Function} return.boundsOf - 取得單筆商品的上下限與預設值
 * @returns {Function} return.isSelectable - 判斷單筆商品是否可選
 * @returns {Function} return.getId - 取得單筆商品的唯一 ID
 *
 * @example
 * const {
 *   selectedIds,
 *   priceById,
 *   qtyById,
 *   grandTotal,
 *   toggleAll,
 *   toggleOne,
 *   onChangePrice,
 *   onChangeQty,
 * } = useSalesAllowance(items, { listDisabled: false });
 *
 * toggleOne(101, true); // 勾選某商品
 * onChangeQty(items[0], 2); // 修改該商品的退貨數量
 * console.log(grandTotal); // 印出總退費金額
 */

const useSalesAllowance = (items, {listDisabled = false, onAggregate} = {}) => {
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [priceById, setPriceById] = useState(() => ({})); // { [orderItemID]: number }
    const [qtyById, setQtyById] = useState(() => ({})); // { [orderItemID]: number }
    const [errorById, setErrorById] = useState(() => ({})); // { [orderItemID]: boolean }

    const rowMap = useMemo(() => {
            const map = new Map();
            (items || []).forEach(row => map.set(getId(row), row));
            return map;
        },
        // eslint-disable-next-line
        [items]);

    const resetToDefaults = useCallback((row) => {
        const id = getId(row);
        const b = boundsOf(row);
        setErrorById((p) => ({...p, [id]: false}));
        if (row.onlyAllowancePrice) {
            setPriceById((p) => ({...p, [id]: b.defaultPrice}));
            setQtyById((p) => {
                const {[id]: _, ...rest} = p;
                return rest;
            });
        } else {
            setQtyById((p) => ({...p, [id]: b.defaultQty}));
            setPriceById((p) => {
                const {[id]: _, ...rest} = p;
                return rest;
            });
        }
    }, []);

    // 勾選
    const toggleAll = useCallback((checked) => {
            const nextSelected = new Set();
            const nextPrice = {};
            const nextQty = {};
            const nextErr = {};

            (items || []).forEach((row) => {
                const id = getId(row);
                const selectable = isSelectable(row, {listDisabled});
                const b = boundsOf(row);

                // 先把預設值建好
                nextErr[id] = false;
                if (row.onlyAllowancePrice) {
                    nextPrice[id] = b.defaultPrice;
                } else {
                    nextQty[id] = b.defaultQty;
                }

                // 再決定是否加入選取
                if (checked && selectable) nextSelected.add(id);
            });

            setSelectedIds(nextSelected);
            setPriceById(nextPrice);
            setQtyById(nextQty);
            setErrorById(nextErr);
        },
        // eslint-disable-next-line
        [items, listDisabled]);

    const toggleOne = useCallback((id, _checkedIgnored, rowArg) => {
            const row = rowArg || rowMap.get(id);
            if (isEmpty(row)) return;

            setSelectedIds((prev) => {
                const next = new Set(prev);
                if (next.has(id)) {
                    next.delete(id);
                } else {
                    next.add(id);
                }
                return next;
            });

            // 無論選或不選，都重設到預設值（避免殘留舊輸入）
            resetToDefaults(row);
        },
        // eslint-disable-next-line
        [rowMap, resetToDefaults]);

    // 輸入
    const onChangePrice = useCallback((row, val) => {
            const id = getId(row);
            const v = Number(val);
            setPriceById((p) => ({...p, [id]: v}));
            setErrorById((p) => {
                const {defaultQty} = boundsOf(row);
                const qty = row.onlyAllowancePrice ? 1 : (qtyById[id] || defaultQty);
                return {...p, [id]: validatePrice(row, v) || validateQty(row, qty)};
            });
        },
        // eslint-disable-next-line
        [qtyById]);

    const onChangeQty = useCallback((row, val) => {
            const id = getId(row);
            const v = Number(val);
            setQtyById((prev) => ({...prev, [id]: v}));
            setErrorById((prev) => {
                const b = boundsOf(row);
                const price = row.onlyAllowancePrice ? (priceById[id] || b.defaultPrice) : row.salePrice;
                return {...prev, [id]: validateQty(row, v) || validatePrice(row, Number(price))};
            });
        },
        // eslint-disable-next-line
        [priceById]);

    /// 列小計（純計算）
    const rowTotal = useCallback((row) => {
            const id = getId(row);
            if (!selectedIds.has(id)) return 0;
            return calcRowTotal(row, priceById[id], qtyById[id]);
        },
        // eslint-disable-next-line
        [selectedIds, priceById, qtyById]);

    // 合計
    const grandTotal = useMemo(() => {
            const total = items.reduce((sum, row) => sum + rowTotal(row), 0);
            if (isFunction(onAggregate)) {
                onAggregate(total, {selectedIds: Array.from(selectedIds), priceById, qtyById, errorById});
            }
            return total;
        },
        // eslint-disable-next-line
        [items, rowTotal, onAggregate, selectedIds, priceById, qtyById, errorById]);

    // 衍生狀態
    const selectableCount = useMemo(() => items.filter(r => isSelectable(r, {listDisabled})).length, [items, listDisabled]);
    const isAllChecked = selectableCount > 0 && selectedIds.size === selectableCount;
    const isIndeterminate = selectedIds.size > 0 && selectedIds.size < selectableCount;
    const showSelectedMode = selectedIds.size > 0;

    return {
        // state
        selectedIds, priceById, qtyById, errorById,
        isAllChecked, isIndeterminate, showSelectedMode, grandTotal,
        // handlers
        toggleAll, toggleOne, onChangePrice, onChangeQty,
        // helpers
        rowTotal, boundsOf, isSelectable, getId,
    }
};

export default useSalesAllowance;
